import { useRef, useEffect, useState } from 'react'

/**
 * Hook to detect if an element's content is overflowing
 * @returns A ref to attach to the element and a boolean indicating overflow
 */
export const useOverflow = <T extends HTMLElement = HTMLDivElement>() => {
  const ref = useRef<T>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const checkOverflow = () => {
      if (ref.current) {
        // Ensure element is actually rendered and has dimensions
        const element = ref.current
        
        // Skip check if element is not visible or has no width
        if (element.offsetWidth === 0 || element.offsetHeight === 0) {
          setIsOverflowing(false)
          return
        }
        
        // Check for horizontal overflow (most common case in tables)
        // scrollWidth includes content that's not visible due to overflow
        // clientWidth is the visible width
        // Use Math.ceil to account for sub-pixel differences
        const scrollWidth = Math.ceil(element.scrollWidth)
        const clientWidth = Math.ceil(element.clientWidth)
        const hasHorizontalOverflow = scrollWidth > clientWidth
        
        // Also check for vertical overflow (less common but possible)
        const scrollHeight = Math.ceil(element.scrollHeight)
        const clientHeight = Math.ceil(element.clientHeight)
        const hasVerticalOverflow = scrollHeight > clientHeight
        
        const overflowing = hasHorizontalOverflow || hasVerticalOverflow
        
        // Only update state if it changed to avoid unnecessary re-renders
        setIsOverflowing(prev => {
          if (prev !== overflowing) {
            return overflowing
          }
          return prev
        })
      }
    }

    // Use double requestAnimationFrame to ensure DOM is fully rendered and layout is calculated
    let rafId2: number | undefined
    const rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        checkOverflow()
      })
    })

    // Small delay to ensure content is rendered
    const timeoutId = setTimeout(checkOverflow, 0)

    // Check overflow on window resize
    window.addEventListener('resize', checkOverflow)
    
    // Store observers for cleanup
    let resizeObserver: ResizeObserver | null = null
    let mutationObserver: MutationObserver | null = null

    // Setup observers after a brief delay to ensure element is in DOM
    const setupTimeout = setTimeout(() => {
      if (!ref.current) return

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(checkOverflow)
        })
        resizeObserver.observe(ref.current)
      }

      if (window.MutationObserver) {
        mutationObserver = new MutationObserver(() => {
          requestAnimationFrame(checkOverflow)
        })
        mutationObserver.observe(ref.current, {
          childList: true,
          subtree: true,
          characterData: true,
        })
      }
    }, 50)

    // Also check when content is fully rendered
    const delayedCheck = setTimeout(() => {
      checkOverflow()
    }, 200)

    return () => {
      if (rafId1) cancelAnimationFrame(rafId1)
      if (rafId2) cancelAnimationFrame(rafId2)
      clearTimeout(timeoutId)
      clearTimeout(delayedCheck)
      clearTimeout(setupTimeout)
      window.removeEventListener('resize', checkOverflow)
      if (resizeObserver && ref.current) {
        resizeObserver.unobserve(ref.current)
      }
      if (mutationObserver) {
        mutationObserver.disconnect()
      }
    }
  }, [])

  return { ref, isOverflowing }
}
